import { FunctionComponent, MouseEvent, useEffect, useRef, useState } from "react";

import { faCheck, faCopy, faPencil } from "@fortawesome/pro-regular-svg-icons";
import { faBuilding, faUser } from "@fortawesome/pro-solid-svg-icons";
import { IconDefinition } from "@fortawesome/fontawesome-svg-core";

import { Icon } from "@/components";
import { copyText } from "@/utils/copyText";

export type InfoCardPopoverKind = "lead" | "company";

export interface InfoCardPopoverRow {
	label: string;
	value: string | number | null | undefined;
	/** When true the value wraps onto multiple lines instead of truncating. */
	long?: boolean;
}

interface InfoCardPopoverProps {
	kind: InfoCardPopoverKind;
	rows: InfoCardPopoverRow[];
	/** Told after a successful copy; the popover writes the clipboard itself. */
	onCopy?: (value: string) => void;
	/** When provided, renders an "Edit contact info" footer link that triggers this. */
	onEdit?: () => void;
}

const HEAD: Record<InfoCardPopoverKind, { icon: IconDefinition; label: string }> = {
	lead: { icon: faUser, label: "Lead" },
	company: { icon: faBuilding, label: "Company" },
};

const stop = (e: MouseEvent) => {
	e.preventDefault();
	e.stopPropagation();
};

const InfoCardRow: FunctionComponent<{
	label: string;
	value: string;
	long?: boolean;
	onCopy?: (value: string) => void;
}> = ({ label, value, long, onCopy }) => {
	const [copied, setCopied] = useState(false);
	const copiedTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	const mountedRef = useRef(true);

	useEffect(() => {
		mountedRef.current = true;

		return () => {
			mountedRef.current = false;

			if (copiedTimerRef.current) {
				clearTimeout(copiedTimerRef.current);
			}
		};
	}, []);

	const copy = (e: MouseEvent) => {
		stop(e);
		void copyText(value).then((copied) => {
			if (!copied || !mountedRef.current) {
				return;
			}

			onCopy?.(value);
			setCopied(true);

			if (copiedTimerRef.current) {
				clearTimeout(copiedTimerRef.current);
			}

			copiedTimerRef.current = setTimeout(() => setCopied(false), 1000);
		});
	};

	return (
		<div
			className={`group/val cursor-pointer rounded-md px-2 py-1.5 transition-colors ${
				copied ? "bg-status-success-light" : "hover:bg-gray-50"
			}`}
			onClick={copy}
		>
			<div className="mb-0.5 text-[9.5px] font-bold uppercase tracking-wide text-text-meta">
				{label}
			</div>
			<div className="flex items-start gap-2">
				<span
					className={`flex-1 text-[12.5px] ${
						long ? "whitespace-normal leading-snug text-text-meta" : "truncate text-gray-900"
					} ${copied ? "font-medium text-status-success" : ""}`}
				>
					{value}
				</span>
				<Icon
					className={`mt-0.5 h-[14px] w-[14px] shrink-0 text-text-meta transition-opacity ${
						copied ? "text-status-success opacity-100" : "opacity-0 group-hover/val:opacity-100"
					}`}
					icon={copied ? faCheck : faCopy}
				/>
			</div>
		</div>
	);
};

/**
 * Hover popover for name / company text matching the v6 mockup:
 * a labelled header, one copyable row per field (label + value, value-only
 * copies on click with a green flash), long rows wrap. Empty rows are hidden.
 */
export const InfoCardPopover: FunctionComponent<InfoCardPopoverProps> = ({
	kind,
	rows,
	onCopy,
	onEdit,
}) => {
	const head = HEAD[kind];
	const visibleRows = rows.filter(
		(row) => row.value !== null && row.value !== undefined && String(row.value).trim() !== ""
	);

	// Without an edit action an empty popover has nothing to show; with one we
	// still render so users can fix a missing/empty name.
	if (visibleRows.length === 0 && !onEdit) {
		return null;
	}

	return (
		<div className="w-[272px]">
			<div className="flex items-center gap-1.5 px-2 pb-1 pt-0.5 text-[10px] font-bold uppercase tracking-wide text-text-meta">
				<Icon className="h-[12px] w-[12px]" icon={head.icon} />
				{head.label}
			</div>
			{/* Only the rows scroll; the edit footer below stays pinned and visible
			    so it never falls below the tooltip's fold on tall cards. */}
			{visibleRows.length > 0 && (
				<div className="max-h-[320px] overflow-y-auto">
					{visibleRows.map((row) => (
						<InfoCardRow
							key={row.label}
							label={row.label}
							long={row.long}
							value={String(row.value)}
							onCopy={onCopy}
						/>
					))}
				</div>
			)}
			{onEdit && (
				<>
					<div className="my-1 h-px bg-gray-200" />
					<button
						className="flex w-full items-center justify-center gap-1.5 rounded-md py-1.5 text-[12.5px] font-semibold text-blue-action hover:bg-gray-50"
						type="button"
						onClick={(e) => {
							stop(e);
							onEdit();
						}}
					>
						<Icon className="h-[14px] w-[14px]" icon={faPencil} />
						Edit contact info
					</button>
				</>
			)}
		</div>
	);
};
